export const SECTION_SYNTAX = 0;
export const ACT_SYNTAX = 1;

/**
 * Default format is:
 *

Act 1 - Introduction:
Chapter 1:
Summary text 1

Chapter 2:
Summary text 2

Act 2 - After:
Chapter 1:
Summary text 3

Chapter 2:
Summary text 4

===
We also allow:

Act 1:
Chapter 1:

Act 1
Chapter 1

*/

export default class Outline {
  // Instantiate the Outline object with raw text from the outline box
  constructor(text) {
    this.text = text;
    this.parseText();
  }

  // Parses the outline text into json
  parseText() {
    if (!this.text || this.text.trim().length === 0) {
      // defaults if blank
      this.sections = [];
      this.syntax = ACT_SYNTAX;
      return;
    }

    const sections = [];
    let currentSection = null;
    let currentScene = null;
    const lines = this.text.split("\n");
    let outlineSyntax = null;

    for (const line of lines) {
      const sectionMatch = line.match(/(Act|Section)\s*(\d+)(?:\s*[-\s]+\s*(.+))?:*/i);
      const partMatch = line.match(/^\s*(Chapter|Part) (\d+):?(.*)$/i);

      if (sectionMatch) {
        // Start of a new section
        let [, sectionType, sectionNumber, sectionTitle] = sectionMatch;

        if (sectionTitle === undefined) {
          sectionTitle = "Untitled";
        }

        currentSection = { title: sectionTitle.replace(":", ""), scenes: [] };
        sections.push(currentSection);

        // Set outlineSyntax
        if (!outlineSyntax) {
          outlineSyntax = sectionType.toLowerCase() === "act" ? ACT_SYNTAX : SECTION_SYNTAX;
        }
      } else if (partMatch) {
        if (!currentSection) {
          // skip the line
          continue;
        }

        // Start of a new scene
        let [, , sceneNumber, sameLineContent] = partMatch;
        currentScene = { contents: sameLineContent ? sameLineContent + "\n" : "" };
        currentSection.scenes.push(currentScene);
      } else if (currentScene) {
        // Add line to current scene content
        currentScene.contents += line + "\n";
      }
    }

    // Trim whitespace from scene content
    for (let section of sections) {
      for (let scene of section.scenes) {
        scene.contents = scene.contents.trim();
      }
    }

    this.syntax = outlineSyntax;
    this.sections = sections;
  }

  // Returns the scene object at given section+scene index
  sceneAt(sectionIndex, sceneIndex) {
    return this.sections[sectionIndex]?.scenes[sceneIndex];
  }

  // Whether it uses Act syntax
  get isActs() {
    return this.syntax === ACT_SYNTAX;
  }

  // Returns an array of options for all scenes suitable for a grouped selector
  groupedOptions() {
    const groupedScenes = [];
    let value = 0;
    let actOrSectionNumber = 1;
    let chapterOrPartNumber = 1;
    const actOrSectionLabel = this.syntax === ACT_SYNTAX ? "Act" : "Section";
    const chapterOrPartLabel = this.syntax === ACT_SYNTAX ? "Chapter" : "Part";

    for (const section of this.sections) {
      const actOrSectionGroup = {
        label: `${actOrSectionLabel} ${actOrSectionNumber}`,
        options: [],
      };

      // Reset chapterOrPartNumber for each section when outlineSyntax is "SectionPart"
      if (this.syntax === SECTION_SYNTAX) {
        chapterOrPartNumber = 1;
      }

      for (const scene of section.scenes) {
        actOrSectionGroup.options.push({
          value,
          label: `${chapterOrPartLabel} ${chapterOrPartNumber}`,
          summary: scene.contents, // Include the scene summary in the options
        });
        value++;
        chapterOrPartNumber++;
      }

      groupedScenes.push(actOrSectionGroup);
      actOrSectionNumber++;
    }

    return groupedScenes;
  }

  // Given an absolute scene index, returns the [section, scene] tuple
  sectionAndsceneAtIndex(sceneIndex) {
    let index = 0;
    let sectionIdx = 0;
    let sceneIdx = 0;

    for (const section of this.sections) {
      sceneIdx = 0;
      // console.log(section.scenes);

      for (let scene of section.scenes) {
        if (index === sceneIndex) {
          return [sectionIdx, sceneIdx];
        }

        sceneIdx++;
        index++;
      }

      sectionIdx++;
    }
    return null;
  }

  get isEmpty() {
    return this.sections.length === 0;
  }

  /**
   * Given a section and scene, figures out which sequential Chapter number this is
   * @param {Number} sectionIndex
   * @param {Number} sceneIndex
   * @returns
   */
  chapterAtIndex(sectionIndex, sceneIndex) {
    if (this.isEmpty) {
      // If there are no sections, then this is an empty outline, and we presume chapters are at 1
      return 1;
    }

    let chapter = 1;
    let currentSectionIndex = 0;
    let currentSceneIndex = 0;

    for (const section of this.sections) {
      currentSceneIndex = 0;

      for (const scene of section.scenes) {
        if (sectionIndex === currentSectionIndex && sceneIndex === currentSceneIndex) {
          return chapter;
        }

        currentSceneIndex++;
        chapter++;
      }

      currentSectionIndex++;
    }

    return null;
  }
}
